import { useCallback, useEffect, useMemo, useState } from "react";
import { Coffee, Cookie, Sparkles } from "lucide-react";
import BrownieIcon from "../components/BrownieIcon";
import { api } from "../lib/apiClient";
import { resolveImageUrl } from "../lib/images";

const CATEGORY_ORDER = ["classique", "croquant", "gourmand", "premium"];
const CATEGORY_LABELS = {
    classique: "Les classiques",
    croquant: "Les croquants",
    gourmand: "Les gourmands",
    premium: "Les premium",
};
const CATEGORY_TAGLINES = {
    classique: "les indispensables",
    croquant: "ça croque, ça craque",
    gourmand: "pour les vraies",
    premium: "le luxe en bouchée",
};

function formatPrice(price) {
    if (price == null) return "-";
    return `${Number(price).toFixed(2).replace(".", ",")} €`;
}

const TILT_ANGLES = [-3, -2.5, -2, -1.5, 1.5, 2, 2.5, 3];

function pickRandomAngle() {
    return TILT_ANGLES[Math.floor(Math.random() * TILT_ANGLES.length)];
}

function MenuCard({ item, showPrice = false, testid }) {
    const imgSrc = resolveImageUrl(item.image_url);
    // Pick a random angle once per mount → angles change on each page reload.
    const angle = useMemo(() => pickRandomAngle(), []);
    return (
        <div
            className="brand-card-soft overflow-hidden flex items-stretch transition-transform duration-300 hover:!rotate-0"
            style={{ transform: `rotate(${angle}deg)` }}
            data-testid={testid}
        >
            <div className="flex-1 min-w-0 p-5 flex flex-col justify-between gap-2">
                <div>
                    <h4 className="font-display text-xl text-[#2A1408] leading-tight">
                        {item.name}
                    </h4>
                    {item.description && (
                        <p className="text-sm text-[#5C3D2E] mt-1 line-clamp-3">
                            {item.description}
                        </p>
                    )}
                </div>
                {showPrice && (
                    <span className="font-display text-2xl text-[#D6552C]">
                        {formatPrice(item.price)}
                    </span>
                )}
            </div>
            {imgSrc && (
                <div className="w-28 sm:w-32 aspect-square shrink-0 self-stretch overflow-hidden">
                    <img
                        src={imgSrc}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                </div>
            )}
        </div>
    );
}

export default function Carte() {
    const [items, setItems] = useState([]);
    const [categoryPrices, setCategoryPrices] = useState({});
    const [ingredients, setIngredients] = useState([]);
    const [cookieMonth, setCookieMonth] = useState(null);

    const fetchAll = useCallback(async () => {
        try {
            const [menuRes, ingRes, cmRes] = await Promise.all([
                api.get("/menu"),
                api.get("/ingredients"),
                api.get("/cookie-of-the-month"),
            ]);
            setItems(menuRes.data.items || []);
            setCategoryPrices(menuRes.data.category_prices || {});
            setIngredients(ingRes.data || []);
            setCookieMonth(cmRes.data);
        } catch (_e) {
            // empty state
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const cookies = useMemo(() => items.filter((i) => i.item_type === "cookie"), [items]);
    const drinks = useMemo(() => items.filter((i) => i.item_type === "drink"), [items]);
    const brownies = useMemo(() => items.filter((i) => i.item_type === "brownie"), [items]);

    return (
        <div data-testid="carte-page">
            {/* HEADER */}
            <section className="bg-[#FFF1E0] border-b-2 border-[#2A1408]">
                <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20 text-center relative">
                    <span
                        className="hidden md:inline-block absolute top-28 right-[20%] sticker"
                        style={{ transform: "rotate(8deg)" }}
                    >
                        Bon app' !
                    </span>
                    <p className="section-tag">le menu de la maison</p>
                    <h1 className="font-display text-7xl md:text-9xl text-[#2A1408] leading-[0.85] mt-2">
                        La carte.
                    </h1>
                    <p className="mt-5 text-[#5C3D2E] max-w-2xl mx-auto text-lg">
                        Recettes maison, ingrédients sélectionnés, cuissons soignées.
                    </p>
                </div>
            </section>

            {/* INGREDIENTS */}
            <section className="bg-[#B07B66] text-[#FFF1E0]">
                <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
                    <div className="grid md:grid-cols-3 gap-10 items-start">
                        <div className="md:col-span-1">
                            <p className="font-handwritten text-3xl text-[#FFF1E0]">
                                la qualité
                            </p>
                            <h2 className="font-display text-5xl md:text-6xl mt-1 leading-[0.9]">
                                Provenance des ingrédients.
                            </h2>
                            <p className="mt-4 text-[#F5E6D3]/95">
                                Un bon cookie commence par une bonne matière. On choisit
                                chaque ingrédient avec soin.
                            </p>
                        </div>
                        <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
                            {ingredients.map((ing, i) => (
                                <div
                                    key={ing.id}
                                    className={`bg-[#FFF1E0] text-[#2A1408] p-5 rounded-xl border-2 border-[#2A1408] ${i % 3 === 0 ? "tilt-l" : i % 3 === 1 ? "" : "tilt-r"}`}
                                    style={{ boxShadow: "4px 4px 0 #2A1408" }}
                                    data-testid={`ingredient-${ing.id}`}
                                >
                                    <p className="font-display text-xl text-[#2A1408]">
                                        {ing.name}
                                    </p>
                                    <p className="text-xs uppercase tracking-widest text-[#B07B66] mt-1 font-bold">
                                        {ing.origin}
                                    </p>
                                    {ing.description && (
                                        <p className="mt-2 text-sm text-[#5C3D2E]">
                                            {ing.description}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* COOKIES PAR CATEGORIE */}
            <section className="max-w-6xl mx-auto px-6 sm:px-10 py-24">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
                    <div>
                        <h2 className="font-display text-5xl md:text-6xl text-[#2A1408] flex items-center gap-3">
                            <Cookie strokeWidth={2.2} className="text-[#B07B66]" size={56} />
                            Les cookies.
                        </h2>
                    </div>
                </div>

                <div className="space-y-16">
                    {CATEGORY_ORDER.map((cat) => {
                        const list = cookies.filter((c) => c.category === cat);
                        if (list.length === 0) return null;
                        return (
                            <div key={cat} data-testid={`category-${cat}`}>
                                <div className="flex items-end justify-between border-b-2 border-[#2A1408] pb-3 mb-7 flex-wrap gap-2">
                                    <div>
                                        <p className="font-handwritten text-2xl text-[#B07B66]">
                                            {CATEGORY_TAGLINES[cat]}
                                        </p>
                                        <h3 className="font-display text-3xl md:text-4xl text-[#2A1408]">
                                            {CATEGORY_LABELS[cat]}
                                        </h3>
                                    </div>
                                    <span
                                        className="font-display text-4xl md:text-5xl text-[#D6552C]"
                                        style={{ fontWeight: 800 }}
                                    >
                                        {formatPrice(categoryPrices[cat])}
                                    </span>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {list.map((c) => (
                                        <MenuCard
                                            key={c.id}
                                            item={c}
                                            testid={`menu-item-${c.id}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* COOKIE DU MOIS */}
            {cookieMonth && (
                <section className="max-w-3xl mx-auto px-6 sm:px-10 pb-20">
                    <div className="rounded-3xl overflow-visible grid md:grid-cols-5 bg-[#2A1408] text-[#FFF1E0] border-4 border-[#2A1408] relative">
                        <span
                            className="absolute -top-4 -right-6 sticker z-10 shadow-lg"
                            style={{ transform: "rotate(12deg)" }}
                        >
                            <Sparkles size={14} className="inline mr-1" /> spécial
                        </span>
                        <div className="md:col-span-2 overflow-hidden rounded-l-3xl">
                            {cookieMonth.image_url && (
                                <img
                                    src={resolveImageUrl(cookieMonth.image_url)}
                                    alt={cookieMonth.name}
                                    className="w-full h-full object-cover min-h-[200px] max-h-[260px]"
                                />
                            )}
                        </div>
                        <div className="md:col-span-3 p-6 flex flex-col justify-center">
                            <p className="font-handwritten text-2xl text-[#C99889]">
                                le cookie du mois
                            </p>
                            <h3 className="font-display text-3xl mt-1 leading-[0.95]">
                                {cookieMonth.name}
                            </h3>
                            {cookieMonth.description && (
                                <p className="mt-3 text-[#F5E6D3]/90 text-sm max-w-md">
                                    {cookieMonth.description}
                                </p>
                            )}
                            {cookieMonth.price != null && (
                                <p className="mt-4 font-display text-2xl text-[#C99889]">
                                    {formatPrice(cookieMonth.price)}
                                </p>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* BROWNIES */}
            {brownies.length > 0 && (
                <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-20">
                    <div className="border-t-2 border-dashed border-[#E4D1BB] mb-12" />
                    <h2 className="font-display text-4xl md:text-5xl text-[#2A1408] mb-7 flex items-center gap-3">
                        <BrownieIcon
                            size={48}
                            className="text-[#B07B66]"
                        />
                        Les brownies.
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {brownies.map((b) => (
                            <MenuCard
                                key={b.id}
                                item={b}
                                showPrice
                                testid={`brownie-${b.id}`}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* BOISSONS */}
            <section className="bg-[#FFF1E0] border-y-2 border-[#2A1408]">
                <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                        <h2 className="font-display text-4xl md:text-5xl text-[#2A1408] flex items-center gap-3">
                            <Coffee strokeWidth={2.2} className="text-[#B07B66]" size={44} />
                            Les boissons.
                        </h2>
                    </div>
                    {drinks.length === 0 ? (
                        <p className="text-[#5C3D2E]">Carte des boissons à venir.</p>
                    ) : (
                        (() => {
                            const groups = [
                                { key: "cafe", label: "Café" },
                                { key: "soft_artisanal", label: "Softs artisanaux" },
                                { key: "soft", label: "Softs" },
                            ];
                            const visible = groups
                                .map((g) => ({
                                    ...g,
                                    items: drinks.filter((d) => d.drink_category === g.key),
                                }))
                                .filter((g) => g.items.length > 0);
                            const others = drinks.filter(
                                (d) => !["cafe", "soft_artisanal", "soft"].includes(d.drink_category)
                            );
                            if (others.length > 0) {
                                visible.push({ key: "autres", label: "Autres", items: others });
                            }
                            return (
                                <div className="space-y-10">
                                    {visible.map((g) => (
                                        <div key={g.key} data-testid={`drinks-group-${g.key}`}>
                                            <h3 className="font-handwritten text-3xl text-[#B07B66] mb-3">
                                                {g.label}
                                            </h3>
                                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                                {g.items.map((d) => (
                                                    <div
                                                        key={d.id}
                                                        className="brand-card-soft p-5 flex items-baseline justify-between gap-4"
                                                        data-testid={`drink-${d.id}`}
                                                    >
                                                        <div>
                                                            <p className="font-display text-xl text-[#2A1408]">
                                                                {d.name}
                                                            </p>
                                                            {d.description && (
                                                                <p className="text-sm text-[#5C3D2E] mt-1">
                                                                    {d.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <span className="font-display text-2xl text-[#B07B66] shrink-0">
                                                            {formatPrice(d.price)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()
                    )}
                </div>
            </section>
        </div>
    );
}
